import { ReactNode, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../../minimals/components/iconify";
import { DocumentsContactNote, DocumentsOrderContact } from "../types";
import {
  getAxiosErrorMessage,
  getContactNoteAlertType,
  getDisplayContactNotes
} from "../utils/orderDocumentAlerts";

export function ContactCard({
  contact,
  orderNumber,
  emailSubjectLine,
  onContactsChanged,
  onEdit
}: {
  contact: DocumentsOrderContact;
  orderNumber: string;
  emailSubjectLine?: string;
  onContactsChanged: () => Promise<void>;
  onEdit: (contact: DocumentsOrderContact) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [callingPhone, setCallingPhone] = useState<string | null>(null);
  const [callMessage, setCallMessage] = useState<string | null>(null);
  const [callError, setCallError] = useState<string | null>(null);
  const [contactNoteToResolve, setContactNoteToResolve] = useState<DocumentsContactNote | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  const emails = getContactEmails(contact);
  const addressLines = getAddressLines(contact);
  const displayContactNotes = getDisplayContactNotes(contact);
  const hasDetails = Boolean(
    emails.length ||
      contact.phone ||
      contact.cell ||
      contact.fax ||
      displayContactNotes.length ||
      contact.clientInstructions ||
      addressLines.length ||
      contact.referenceNumber ||
      contact.licenseNumber
  );
  const isEmpty = !contact.name;
  const relationship = getContactRelationship(contact.type);
  const phonebookHref = contact.lookupCode
    ? `/phonebook/contact/profile?lookupcode=${encodeURIComponent(contact.lookupCode)}`
    : "";
  const companyHref = contact.companyLookupCode
    ? `/phonebook/company/profile?lookupcode=${encodeURIComponent(contact.companyLookupCode)}`
    : "";
  const subject = emailSubjectLine ? `?subject=${encodeURIComponent(emailSubjectLine)}` : "";
  const resolveAlertType = getContactNoteAlertType(contactNoteToResolve || undefined);

  const initiateCall = async (phoneNumber: string) => {
    setCallingPhone(phoneNumber);
    setCallError(null);
    setCallMessage(null);

    try {
      await axios.post(
        "/api/vonage/InitiateCall",
        { destinationPhoneNumber: phoneNumber },
        { headers: { "x-suppress-global-error": "true" } }
      );
      setCallMessage(`Calling ${phoneNumber}`);
    } catch {
      setCallError("The call could not be started.");
    } finally {
      setCallingPhone(null);
    }
  };

  const closeResolveDialog = () => {
    if (isResolving) {
      return;
    }

    setContactNoteToResolve(null);
    setResolveNote("");
    setResolveError("");
  };

  const handleResolveContactNote = async () => {
    const trimmedNote = resolveNote.trim();
    if (!trimmedNote) {
      setResolveError("Please enter a note explaining why this was resolved.");
      return;
    }

    try {
      setIsResolving(true);
      setResolveError("");
      await axios.post("/api/orderdocuments/resolveblacklistcontact", {
        orderNumber,
        noteText: contactNoteToResolve?.text,
        resolutionNote: trimmedNote
      });
      await onContactsChanged();
      setContactNoteToResolve(null);
      setResolveNote("");
      setResolveError("");
    } catch (error) {
      setResolveError(getAxiosErrorMessage(error, "Unable to resolve this alert. Please try again."));
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          color: "primary.dark",
          borderBottom: "1px solid",
          borderColor: "divider",
          borderLeft: isEmpty ? "3px solid" : "3px solid transparent",
          borderLeftColor: isEmpty ? "info.light" : "transparent",
          backgroundColor: "background.paper"
        }}
      >
        <Stack spacing={1}>
          <ContactCardHeader
            contact={contact}
            emails={emails}
            relationship={relationship}
            subject={subject}
            callingPhone={callingPhone}
            isExpanded={isExpanded}
            hasDetails={hasDetails}
            onCall={initiateCall}
            onCopyPhone={(phoneNumber) => {
              navigator.clipboard?.writeText(phoneNumber);
              setCallMessage(`Copied ${phoneNumber}`);
            }}
            onEdit={() => onEdit(contact)}
            onToggleExpanded={() => setIsExpanded((current) => !current)}
          />

          <ContactNameRow
            contact={contact}
            phonebookHref={phonebookHref}
            companyHref={companyHref}
          />

          {displayContactNotes.length > 0 && (
            <ContactNotes
              notes={displayContactNotes}
              onResolve={setContactNoteToResolve}
            />
          )}

          <Collapse in={isExpanded} timeout="auto">
            <ContactDetails
              contact={contact}
              emails={emails}
              addressLines={addressLines}
              subject={subject}
              onCall={initiateCall}
            />
          </Collapse>
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(callMessage || callError)}
        autoHideDuration={3500}
        onClose={() => {
          setCallMessage(null);
          setCallError(null);
        }}
      >
        <Alert
          severity={callError ? "error" : "success"}
          onClose={() => {
            setCallMessage(null);
            setCallError(null);
          }}
        >
          {callError || callMessage}
        </Alert>
      </Snackbar>

      <Dialog open={Boolean(contactNoteToResolve)} onClose={closeResolveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Resolve {resolveAlertType === "patriot" ? "patriot" : "blacklisted"} contact</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2">
            Please enter an explanation for resolving this blacklist/patriot alert.
          </Typography>
          {resolveError && <Alert severity="error">{resolveError}</Alert>}
          <TextField size="small"
            label="Explanation"
            multiline
            minRows={4}
            value={resolveNote}
            onChange={(event) => setResolveNote(event.target.value)}
            disabled={isResolving}
            autoFocus
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closeResolveDialog} disabled={isResolving}>
            Cancel
          </Button>
          <Button size="small"
            color={resolveAlertType === "patriot" ? "warning" : "error"}
            variant="contained"
            onClick={handleResolveContactNote}
            disabled={isResolving}
          >
            {isResolving ? "Resolving..." : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ContactCardHeader({
  contact,
  emails,
  relationship,
  subject,
  callingPhone,
  isExpanded,
  hasDetails,
  onCall,
  onCopyPhone,
  onEdit,
  onToggleExpanded
}: {
  contact: DocumentsOrderContact;
  emails: string[];
  relationship: ReactNode;
  subject: string;
  callingPhone: string | null;
  isExpanded: boolean;
  hasDetails: boolean;
  onCall: (phoneNumber: string) => void;
  onCopyPhone: (phoneNumber: string) => void;
  onEdit: () => void;
  onToggleExpanded: () => void;
}) {
  const [openPhoneActions, setOpenPhoneActions] = useState<string | null>(null);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        {contact.isMarketingSource && (
          <Iconify icon="eva:star-fill" width={16} sx={{ color: "#ffae00", flexShrink: 0 }} />
        )}
        <Typography
          variant="subtitle1"
          sx={{
            color: "#ec7258",
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 1.25,
            overflowWrap: "anywhere"
          }}
        >
          {relationship}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
        <Tooltip title="Edit contact">
          <IconButton size="small" onClick={onEdit} sx={contactActionIconSx}>
            <Iconify icon="eva:edit-fill" width={16} />
          </IconButton>
        </Tooltip>
        {emails.length > 0 && (
          <>
            <Tooltip title="Copy email">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard?.writeText(emails.join(";"))}
                sx={contactActionIconSx}
              >
                <Iconify icon="eva:copy-fill" width={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send email">
              <IconButton
                size="small"
                component="a"
                href={`mailto:${emails.join(";")}${subject}`}
                sx={contactActionIconSx}
              >
                <Iconify icon="eva:email-fill" width={16} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {contact.phone && (
          <PhoneActionButtons
            phoneNumber={contact.phone}
            icon="solar:phone-calling-bold"
            tooltip={contact.phone}
            actionsLabel={`Phone actions ${contact.phone}`}
            isOpen={openPhoneActions === contact.phone}
            isCalling={callingPhone === contact.phone}
            onToggle={() => setOpenPhoneActions((current) => current === contact.phone ? null : contact.phone!)}
            onCall={() => {
              setOpenPhoneActions(null);
              onCall(contact.phone!);
            }}
            onCopy={() => {
              setOpenPhoneActions(null);
              onCopyPhone(contact.phone!);
            }}
          />
        )}
        {contact.cell && (
          <PhoneActionButtons
            phoneNumber={contact.cell}
            icon="eva:smartphone-fill"
            tooltip={contact.cell}
            actionsLabel={`Cell phone actions ${contact.cell}`}
            isOpen={openPhoneActions === contact.cell}
            isCalling={callingPhone === contact.cell}
            onToggle={() => setOpenPhoneActions((current) => current === contact.cell ? null : contact.cell!)}
            onCall={() => {
              setOpenPhoneActions(null);
              onCall(contact.cell!);
            }}
            onCopy={() => {
              setOpenPhoneActions(null);
              onCopyPhone(contact.cell!);
            }}
          />
        )}
        <IconButton
          size="small"
          disabled={!hasDetails}
          onClick={onToggleExpanded}
          sx={{
            p: 0.5,
            color: hasDetails ? "primary.dark" : "grey.400",
            "&:hover": {
              color: hasDetails ? "info.main" : "grey.400",
              backgroundColor: "transparent"
            }
          }}
        >
          <Iconify icon={isExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} width={16} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function PhoneActionButtons({
  phoneNumber,
  icon,
  tooltip,
  actionsLabel,
  isOpen,
  isCalling,
  onToggle,
  onCall,
  onCopy
}: {
  phoneNumber: string;
  icon: string;
  tooltip: string;
  actionsLabel: string;
  isOpen: boolean;
  isCalling: boolean;
  onToggle: () => void;
  onCall: () => void;
  onCopy: () => void;
}) {
  return (
    <ClickAwayListener onClickAway={() => isOpen && onToggle()}>
      <Box sx={{ position: "relative", width: 22, height: 22, flexShrink: 0, display: "inline-flex" }}>
        <Tooltip title={isOpen ? "" : tooltip} placement="top">
          <IconButton
            size="small"
            aria-label={actionsLabel}
            aria-expanded={isOpen}
            onClick={onToggle}
            sx={contactActionIconSx}
          >
            <Iconify icon={icon} width={16} />
          </IconButton>
        </Tooltip>
        <Collapse in={isOpen} timeout={140} unmountOnExit>
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 7px)",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 20,
              px: 0.45,
              py: 0.45,
              borderRadius: 0.75,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: alpha("#0E0E65", 0.28),
              boxShadow: "0 4px 8px rgba(14, 14, 101, 0.16)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -5,
                left: "50%",
                width: 8,
                height: 8,
                transform: "translateX(-50%) rotate(45deg)",
                backgroundColor: "background.paper",
                borderTop: "1px solid",
                borderLeft: "1px solid",
                borderColor: alpha("#0E0E65", 0.28)
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.45}>
              <Tooltip title="Call">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Call ${phoneNumber}`}
                    onClick={onCall}
                    disabled={isCalling}
                    sx={phoneSubActionIconSx}
                  >
                    <Iconify icon="mdi:phone-outgoing" width={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Copy phone">
                <IconButton
                  size="small"
                  aria-label={`Copy phone ${phoneNumber}`}
                  onClick={onCopy}
                  sx={phoneSubActionIconSx}
                >
                  <Iconify icon="eva:copy-fill" width={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}

function ContactNameRow({
  contact,
  phonebookHref,
  companyHref
}: {
  contact: DocumentsOrderContact;
  phonebookHref: string;
  companyHref: string;
}) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "primary.dark",
            fontWeight: 600,
            fontSize: 13,
            lineHeight: 1.3,
            textTransform: contact.name ? "capitalize" : "none",
            overflowWrap: "anywhere"
          }}
        >
          {contact.name ? (
            contact.lookupCode ? (
              <Link href={phonebookHref} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
                {contact.name}
              </Link>
            ) : (
              contact.name
            )
          ) : (
            "????"
          )}
        </Typography>
      </Box>

      {contact.payeeName && contact.name !== contact.payeeName && (
        <Typography
          variant="body2"
          sx={{
            color: "primary.dark",
            fontWeight: 500,
            fontSize: 13,
            textAlign: "right",
            minWidth: 0,
            overflowWrap: "anywhere"
          }}
        >
          <Iconify icon="heroicons-outline:office-building" width={16} sx={{ mr: 0.5, verticalAlign: "text-bottom" }} />
          {companyHref ? (
            <Link href={companyHref} target="_blank" rel="noopener noreferrer" underline="none" color="inherit">
              {contact.payeeName}
            </Link>
          ) : (
            contact.payeeName
          )}
        </Typography>
      )}
    </Stack>
  );
}

function ContactNotes({
  notes,
  onResolve
}: {
  notes: DocumentsContactNote[];
  onResolve: (note: DocumentsContactNote) => void;
}) {
  return (
    <Stack spacing={0.5}>
      {notes.map((contactNote, index) => {
        const noteAlertType = getContactNoteAlertType(contactNote);
        const isAlert = Boolean(noteAlertType);

        return (
          <Box
            key={`${contactNote.text}-${index}`}
            sx={{
              border: "1px solid",
              borderColor: isAlert
                ? noteAlertType === "patriot"
                  ? "#f4d27a"
                  : "#f0a39a"
                : "divider",
              borderLeft: "4px solid",
              borderLeftColor: isAlert
                ? noteAlertType === "patriot"
                  ? "#ed9d00"
                  : "#d32f2f"
                : "divider",
              backgroundColor: isAlert
                ? noteAlertType === "patriot"
                  ? "#fff8e1"
                  : "#fff1f0"
                : "background.neutral",
              color: isAlert
                ? noteAlertType === "patriot"
                  ? "#6d4c00"
                  : "#8a1f11"
                : "text.primary",
              borderRadius: 0.75,
              p: 0.75,
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              justifyContent: "space-between"
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {contactNote.text}
            </Typography>
            {noteAlertType && (
              <Button
                color={noteAlertType === "patriot" ? "warning" : "error"}
                size="small"
                variant="contained"
                onClick={() => onResolve(contactNote)}
                sx={{ flexShrink: 0, minWidth: 72, fontSize: 12, height: 26 }}
              >
                Resolve
              </Button>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function ContactDetails({
  contact,
  emails,
  addressLines,
  subject,
  onCall
}: {
  contact: DocumentsOrderContact;
  emails: string[];
  addressLines: string[];
  subject: string;
  onCall: (phoneNumber: string) => void;
}) {
  return (
    <Box
      sx={{
        pt: 0.5,
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1.5,
        color: "primary.dark"
      }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {emails.map((email) => (
          <Link
            key={email}
            href={`mailto:${email}${subject}`}
            underline="hover"
            sx={{ fontSize: 13, overflowWrap: "anywhere" }}
          >
            {email}
          </Link>
        ))}
        {contact.phone && <ContactDetailButton value={contact.phone} onClick={() => onCall(contact.phone!)} />}
        {contact.cell && (
          <ContactDetailButton label="Cell" value={contact.cell} onClick={() => onCall(contact.cell!)} />
        )}
        {contact.fax && <ContactDetailText label="Fax" value={contact.fax} />}
        {contact.referenceNumber && <ContactDetailText label="Ref" value={contact.referenceNumber} />}
      </Stack>

      <Stack spacing={0.5} sx={{ minWidth: 0, textAlign: "right" }}>
        {addressLines.length > 0 && (
          <Box>
            {addressLines.map((line) => (
              <Typography key={line} variant="body2" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}
        {contact.represents && <ContactDetailText label="Represents" value={contact.represents} />}
        {contact.licenseNumber && <ContactDetailText label="License" value={contact.licenseNumber} />}
      </Stack>

      {contact.clientInstructions && (
        <Box sx={{ gridColumn: "1 / -1", mt: 0.25 }}>
          <ContactDetailText label="Client Instructions" value={contact.clientInstructions} strong />
        </Box>
      )}
    </Box>
  );
}

const contactActionIconSx = {
  width: 22,
  height: 22,
  p: 0,
  borderRadius: 0.5,
  color: "common.white",
  backgroundColor: alpha("#0E0E65", 0.65),
  outline: "2px solid",
  outlineColor: alpha("#0E0E65", 0.65),
  "&:hover": {
    color: "primary.dark",
    backgroundColor: "common.white"
  }
};

const phoneSubActionIconSx = {
  ...contactActionIconSx,
  color: "primary.dark",
  backgroundColor: alpha("#0E0E65", 0.08),
  outlineColor: "transparent",
  "&:hover": {
    color: "common.white",
    backgroundColor: alpha("#0E0E65", 0.75)
  }
};

function getContactRelationship(type?: string) {
  return type === "Buyer's Broker (AKA Selling Agent in Select)" ? (
    <>
      Buyer's Broker <Box component="span" sx={{ fontSize: "0.8em" }}>(AKA Selling Agent)</Box>
    </>
  ) : (
    type || "Contact"
  );
}

function getContactEmails(contact: DocumentsOrderContact) {
  const splitEmails = contact.emailsSplit?.filter(Boolean) ?? [];
  if (splitEmails.length) {
    return splitEmails;
  }

  return contact.email?.split(";").map((email) => email.trim()).filter(Boolean) ?? [];
}

function getAddressLines(contact: DocumentsOrderContact) {
  return [
    contact.address1,
    contact.address2,
    contact.cityStateZip || [contact.city, contact.state, contact.zip].filter(Boolean).join(" ")
  ].filter((line): line is string => Boolean(line));
}

function ContactDetailButton({
  label = "",
  value,
  onClick
}: {
  label?: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="text"
      size="small"
      onClick={onClick}
      sx={{
        justifyContent: "flex-start",
        p: 0,
        minWidth: 0,
        color: "primary.dark",
        fontWeight: 400,
        fontSize: 13,
        textTransform: "none",
        overflowWrap: "anywhere"
      }}
    >
      {label ? `${label}: ${value}` : value}
    </Button>
  );
}

function ContactDetailText({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Typography variant="body2" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
      {label && (
        <Box component="span" sx={{ fontWeight: strong ? 900 : 700 }}>
          {label}:{" "}
        </Box>
      )}
      {value}
    </Typography>
  );
}
